import { routes, type Support, type SupportInput, supportInputSchema, SupportTypes } from '@pmai/contracts';
import { CheckboxField, FormDialog, SelectField, TextField } from '@pmai/ui';
import { useTranslation } from 'react-i18next';
import { useCustomerOptions } from '../../entities/customers/customer-options';
import { useApiQuery } from '../../shared/api/hooks';
import { bind, bindSelect } from '../../shared/forms/form';
import { useEntityForm } from '../../shared/forms/use-entity-form';
import { useEnumLabel } from '../../shared/i18n';

const empty: SupportInput = {
  customerId: '',
  productId: '',
  isActive: true,
  needsReview: false,
  startDate: '',
  endDate: '',
  supportType: '',
  monthlyFee: '',
};

const valuesOf = (s: Support): SupportInput => ({
  customerId: s.customerId,
  productId: s.productId,
  isActive: s.isActive,
  needsReview: s.needsReview,
  startDate: s.startDate ?? '',
  endDate: s.endDate ?? '',
  supportType: s.supportType ?? '',
  monthlyFee: s.monthlyFee ?? '',
});

/**
 * "Pievienot ierakstu" / "Labot support ierakstu" (FR-SUP-003…006): the product list follows the
 * customer; dates, type and fee only while support is in force.
 */
export function SupportDialog({
  open,
  entry,
  onClose,
}: {
  open: boolean;
  entry: Support | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const label = useEnumLabel();
  const customers = useCustomerOptions(open);
  const { form, create, update, dialog } = useEntityForm({
    schema: supportInputSchema,
    open,
    record: entry,
    values: entry ? valuesOf(entry) : empty,
    create: routes.support.create,
    update: routes.support.update,
    invalidates: [routes.support.list, routes.support.gaps],
    onClose,
  });
  const customerId = form.watch('customerId');
  const isActive = form.watch('isActive');
  const products = useApiQuery(
    routes.products.list,
    { params: { id: customerId } },
    { enabled: open && !!customerId },
  );
  const productPlaceholder = !customerId
    ? t('support.chooseCustomerFirst')
    : products.data?.length === 0
      ? t('support.noProducts')
      : t('support.chooseProduct');

  const f = (field: string) => t(`support.fields.${field}`);
  const customerField = bindSelect(form, 'customerId');
  return (
    <FormDialog
      {...dialog}
      title={entry ? t('support.editTitle') : t('support.newTitle')}
      onSubmit={form.handleSubmit((body) =>
        entry
          ? update.mutate({ params: { id: entry.id }, body: { ...body, version: entry.version } })
          : create.mutate({ body }),
      )}
    >
      <SelectField
        label={f('customer')}
        required
        placeholder={t('support.chooseCustomer')}
        options={customers}
        {...customerField}
        onChange={(e) => {
          void customerField.onChange(e);
          form.setValue('productId', '');
        }}
      />
      <SelectField
        label={f('product')}
        required
        placeholder={productPlaceholder}
        options={(products.data ?? []).map((p) => ({ value: p.id, label: p.name }))}
        disabled={!customerId}
        {...bindSelect(form, 'productId')}
      />
      <div className="flex flex-wrap gap-6">
        <CheckboxField label={f('isActive')} {...form.register('isActive')} />
        <CheckboxField label={f('needsReview')} {...form.register('needsReview')} />
      </div>
      {isActive && (
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField label={f('startDate')} type="date" required {...bind(form, 'startDate')} />
          <TextField label={f('endDate')} type="date" {...bind(form, 'endDate')} />
          <SelectField
            label={f('supportType')}
            placeholder={t('common.notSpecified')}
            options={SupportTypes.options.map((o) => ({ value: o.code, label: label(o) }))}
            {...bind(form, 'supportType')}
          />
          <TextField label={f('monthlyFee')} inputMode="decimal" {...bind(form, 'monthlyFee')} />
        </div>
      )}
    </FormDialog>
  );
}
